import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Row, Col, Card, Statistic, Table, Tag, Button, Space, Typography, Spin } from 'antd'
import {
  UserOutlined,
  BookOutlined,
  ShopOutlined,
  DollarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  EyeOutlined,
  EditOutlined,
  BarChartOutlined,
} from '@ant-design/icons'
import { Column } from '@ant-design/charts'
import { useAuthStore } from '@/store/auth'
import { merchantApi, merchantVenueApi, scriptApi, userApi } from '@/services/api'

const { Title, Text } = Typography

interface DashboardStats {
  total_users: number
  active_users: number
  total_merchants: number
  total_scripts: number
  total_venues: number
  total_revenue: number
  monthly_growth: number
  user_growth: number
  script_growth: number
  venue_growth: number
  revenue_growth: number
}

interface RecentActivity {
  id: string
  user: string
  action: string
  target: string
  timestamp: string
  type: 'user' | 'script' | 'venue' | 'system'
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    total_users: 0,
    active_users: 0,
    total_merchants: 0,
    total_scripts: 0,
    total_venues: 0,
    total_revenue: 0,
    monthly_growth: 0,
    user_growth: 0,
    script_growth: 0,
    venue_growth: 0,
    revenue_growth: 0,
  })
  const [loading, setLoading] = useState(true)

  const [recentActivities] = useState<RecentActivity[]>([
    {
      id: '1',
      user: '张三',
      action: '创建了新剧本',
      target: '《神秘庄园》',
      timestamp: '2分钟前',
      type: 'script',
    },
    {
      id: '2',
      user: '李四',
      action: '预订了场馆',
      target: 'Room-101',
      timestamp: '5分钟前',
      type: 'venue',
    },
    {
      id: '3',
      user: '王五',
      action: '完成了KYC认证',
      target: '',
      timestamp: '10分钟前',
      type: 'user',
    },
    {
      id: '4',
      user: '系统',
      action: 'AI世界实例已启动',
      target: 'World-001',
      timestamp: '15分钟前',
      type: 'system',
    },
  ])

  const [chartData] = useState([
    { month: '1月', users: 100, scripts: 20, venues: 10 },
    { month: '2月', users: 150, scripts: 25, venues: 12 },
    { month: '3月', users: 200, scripts: 30, venues: 15 },
    { month: '4月', users: 280, scripts: 35, venues: 18 },
    { month: '5月', users: 350, scripts: 40, venues: 20 },
    { month: '6月', users: 420, scripts: 45, venues: 22 },
  ])

  const { user } = useAuthStore()
  const navigate = useNavigate()

  // 仪表盘只聚合当前仍由 admin-portal-api 负责的 A 档接口。
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const [userResponse, merchantResponse, scriptResponse, venueResponse] =
          await Promise.all([
            userApi.getStats(),
            merchantApi.getMerchants({ page: 1, page_size: 1 }),
            scriptApi.getScriptStatistics(),
            merchantVenueApi.getVenues({ page: 1, page_size: 1 }),
          ])

        setStats((current) => ({
          ...current,
          total_users: userResponse.data?.total || 0,
          active_users: userResponse.data?.active || 0,
          total_merchants: merchantResponse.data?.total || 0,
          total_scripts: scriptResponse.data?.total_count || 0,
          total_venues: venueResponse.data?.total || 0,
        }))
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()

    // 定期刷新统计数据
    const interval = setInterval(async () => {
      try {
        const [userResponse, merchantResponse, scriptResponse, venueResponse] =
          await Promise.all([
            userApi.getStats(),
            merchantApi.getMerchants({ page: 1, page_size: 1 }),
            scriptApi.getScriptStatistics(),
            merchantVenueApi.getVenues({ page: 1, page_size: 1 }),
          ])

        setStats((current) => ({
          ...current,
          total_users: userResponse.data?.total || 0,
          active_users: userResponse.data?.active || 0,
          total_merchants: merchantResponse.data?.total || 0,
          total_scripts: scriptResponse.data?.total_count || 0,
          total_venues: venueResponse.data?.total || 0,
        }))
      } catch (error) {
        console.error('刷新数据失败:', error)
      }
    }, 30000) // 30秒刷新一次

    return () => clearInterval(interval)
  }, [])

  const statConfig = [
    {
      title: '总用户数',
      value: stats.total_users,
      prefix: <UserOutlined />,
      trend: stats.user_growth,
      color: '#1890ff',
    },
    {
      title: '剧本数量',
      value: stats.total_scripts,
      prefix: <BookOutlined />,
      trend: stats.script_growth,
      color: '#52c41a',
    },
    {
      title: '场馆数量',
      value: stats.total_venues,
      prefix: <ShopOutlined />,
      trend: stats.venue_growth,
      color: '#faad14',
    },
    {
      title: '总收入',
      value: stats.total_revenue > 0 ? `¥${(stats.total_revenue / 10000).toFixed(1)}万` : '¥0',
      prefix: <DollarOutlined />,
      trend: stats.revenue_growth,
      color: '#722ed1',
    },
  ]

  const columns = [
    {
      title: '用户',
      dataIndex: 'user',
      key: 'user',
      width: 120,
    },
    {
      title: '操作',
      dataIndex: 'action',
      key: 'action',
      width: 150,
    },
    {
      title: '目标',
      dataIndex: 'target',
      key: 'target',
      render: (text: string) => text || '-',
    },
    {
      title: '时间',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 100,
      align: 'right' as const,
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 80,
      render: (type: string) => {
        const colors = {
          user: 'blue',
          script: 'green',
          venue: 'orange',
          system: 'purple',
        }
        return <Tag color={colors[type as keyof typeof colors]}>{type}</Tag>
      },
    },
  ]

  const chartConfig = {
    data: chartData,
    xField: 'month',
    yField: 'users',
    columnStyle: {
      radius: [4, 4, 0, 0],
    },
    color: '#1890ff',
  }

  return (
    <Spin spinning={loading}>
      <div className="fade-in p-6">
        <div className="mb-6">
          <Title level={2}>
            欢迎回来，{user?.full_name || user?.username}！
          </Title>
          <Text type="secondary">
            这是您的管理仪表盘概览
          </Text>
        </div>

        {/* Statistics Cards */}
        <Row gutter={[16, 16]} className="mb-6">
          {statConfig.map((stat, index) => (
            <Col xs={12} sm={12} md={6} lg={6} xl={6} key={index}>
              <Card hoverable className="card-hover">
                <Statistic
                  title={stat.title}
                  value={stat.value}
                  prefix={
                    <div style={{ color: stat.color, fontSize: '24px' }}>{stat.prefix}</div>}
                  suffix={
                    stat.trend !== 0 && (
                      <span
                        style={{
                          color: stat.trend > 0 ? '#52c41a' : '#ff4d4f',
                          fontSize: '12px',
                        }}
                      >
                        {stat.trend > 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                        {Math.abs(stat.trend)}%
                      </span>
                    )
                  }
                  valueStyle={{ color: stat.color }}
                />
              </Card>
            </Col>
          ))}
        </Row>

        {/* Charts and Activity */}
        <Row gutter={[16, 16]}>
          {/* Growth Chart */}
          <Col xs={24} sm={24} md={16} lg={16} xl={16}>
            <Card
              title="增长趋势"
              extra={
                <Space>
                  <Button type="text" icon={<EyeOutlined />}>查看详情</Button>
                  <Button type="text" icon={<EditOutlined />}>编辑</Button>
                </Space>
              }
            >
              <div style={{ height: '300px' }}>
                <Column {...chartConfig} />
              </div>
            </Card>
          </Col>

          {/* Recent Activity */}
          <Col xs={24} sm={24} md={8} lg={8} xl={8}>
            <Card title="最近活动" extra={<Button type="link">更多</Button>}>
              <Table
                dataSource={recentActivities}
                columns={columns}
                pagination={false}
                size="small"
                showHeader={false}
              />
            </Card>
          </Col>
        </Row>

        {/* Quick Actions */}
        <div className="mt-6">
          <Card title="快捷操作">
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <Button
                  type="primary"
                  block
                  icon={<UserOutlined />}
                  onClick={() => navigate('/users')}
                >
                  添加用户
                </Button>
              </Col>
              <Col span={6}>
                <Button block icon={<BookOutlined />} onClick={() => navigate('/scripts')}>
                  创建剧本
                </Button>
              </Col>
              <Col span={6}>
                <Button block icon={<ShopOutlined />} onClick={() => navigate('/venues')}>
                  添加场馆
                </Button>
              </Col>
              <Col span={6}>
                <Button
                  block
                  icon={<BarChartOutlined />}
                  onClick={() => navigate('/ticketing/session-data')}
                >
                  查看场次数据
                </Button>
              </Col>
            </Row>
          </Card>
        </div>
      </div>
    </Spin>
  )
}

export default Dashboard

// Placeholder components for other pages
export const UserManagement = () => (
  <div>
    <Title level={2}>用户管理</Title>
    <Text>用户管理功能开发中...</Text>
  </div>
)

export const ScriptManagement = () => (
  <div>
    <Title level={2}>剧本管理</Title>
    <Text>剧本管理功能开发中...</Text>
  </div>
)

export const VenueManagement = () => (
  <div>
    <Title level={2}>场馆管理</Title>
    <Text>场馆管理功能开发中...</Text>
  </div>
)

export const AIWorldManagement = () => (
  <div>
    <Title level={2}>AI世界管理</Title>
    <Text>AI世界管理功能开发中...</Text>
  </div>
)

export const Analytics = () => (
  <div>
    <Title level={2}>数据分析</Title>
    <Text>数据分析功能开发中...</Text>
  </div>
)

export const Settings = () => (
  <div>
    <Title level={2}>系统设置</Title>
    <Text>系统设置功能开发中...</Text>
  </div>
)